/* ZYMA product page — sections/zyma-product-main.liquid, snippets/zyma-pd-purchase.liquid, sections/zyma-pd-packs.liquid.
   <zyma-pd-purchase>  keeps the form's hidden variant id + quantity in sync with the visible option, and updates
                       prices, the selection summary and the add-to-cart state. Submission stays with Heritage's
                       <product-form-component> (cart drawer, errors, cart events).
   <zyma-pd-bar>       sticky buy bar shown after the buy options scroll out of view.
   [data-zyma-pd-units] buttons anywhere on the page (pack value table) select that option and scroll to it.
   Bulk totals are an estimate for display; checkout applies the store's automatic discount. */
(() => {
  'use strict';

  const int = (value, fallback = 0) => {
    const number = Math.round(Number(value));
    return Number.isFinite(number) ? number : fallback;
  };

  const fill = (template, values) => String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));

  const formatMoney = (cents, format) => {
    const value = int(cents);
    const number = (places, thousands, decimal) => {
      const [whole, fraction] = (Math.abs(value) / 100).toFixed(places).split('.');
      return (value < 0 ? '-' : '') + whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const template = format && pattern.test(format) ? format : '{{amount}}';
    return template.replace(pattern, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return number(0, ',', '.');
        case 'amount_with_comma_separator': return number(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(0, '.', ',');
        case 'amount_with_apostrophe_separator': return number(2, "'", '.');
        case 'amount_with_space_separator': return number(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return number(0, ' ', ',');
        default: return number(2, ',', '.');
      }
    });
  };

  class ZymaPdPurchase extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.scope = this.closest('[data-zyma-pd]') || document;
      this.moneyFormat = this.closest('[data-money-format]')?.dataset.moneyFormat;
      this.formComponent = this.querySelector('product-form-component');
      this.variantInput = this.querySelector('[data-zyma-pd-variant]');
      this.quantityInput = this.querySelector('[data-zyma-pd-quantity]');
      this.choices = [...this.querySelectorAll('[data-zyma-pd-choice]')];
      this.bulkPanel = this.querySelector('[data-zyma-pd-bulk]');
      this.stepInput = this.querySelector('[data-zyma-pd-step-input]');
      this.bulkUnits = Math.max(2, int(this.dataset.bulkUnits, 5));
      this.bulkPercent = Math.min(100, Math.max(0, Number(this.dataset.bulkPercent) || 0));

      // Switching option restarts the count: packs start at one pack, "5+" starts at its minimum number of bottles.
      this.choices.forEach((choice) =>
        choice.addEventListener('change', () => {
          if (this.stepInput) this.stepInput.value = String(choice.hasAttribute('data-bulk') ? this.bulkUnits : 1);
          this.update();
        }, { signal })
      );
      this.querySelectorAll('[data-zyma-pd-step]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!this.stepInput) return;
          this.stepInput.value = String(this.stepValue() + int(button.dataset.zymaPdStep));
          this.update();
        }, { signal });
      });
      this.stepInput?.addEventListener('change', () => this.update(), { signal });

      const checked = this.checked();
      if (this.choices.length && (!checked || checked.disabled)) {
        const firstAvailable = this.choices.find((choice) => !choice.disabled);
        if (firstAvailable) firstAvailable.checked = true;
      }
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    checked() {
      return this.choices.find((choice) => choice.checked) || null;
    }

    /** "5+" counts bottles, so it starts at its minimum; every other option counts packs from one. */
    stepMin() {
      if (this.checked()?.hasAttribute('data-bulk')) return this.bulkUnits;
      return Math.max(1, int(this.stepInput?.min, 1));
    }

    stepValue() {
      return Math.max(this.stepMin(), int(this.stepInput?.value, this.stepMin()));
    }

    /** Selects the option for a number of bottles. Returns false when no matching option is available. */
    select(units) {
      const wanted = Math.max(1, int(units, 1));
      if (!this.choices.length) {
        if (this.stepInput) this.stepInput.value = String(wanted);
        this.update();
        return true;
      }
      let target = this.choices.find((choice) => !choice.hasAttribute('data-bulk') && int(choice.dataset.units) === wanted && !choice.disabled);
      if (target && this.stepInput) this.stepInput.value = '1';
      if (!target && wanted >= this.bulkUnits) {
        target = this.choices.find((choice) => choice.hasAttribute('data-bulk') && !choice.disabled);
        if (target && this.stepInput) this.stepInput.value = String(wanted);
      }
      if (!target) return false;
      target.checked = true;
      this.update();
      return true;
    }

    update() {
      const choice = this.checked();
      let units = 1;
      let total = 0;
      let compareTotal = 0;
      let submitQuantity = 1;
      let available = true;
      let variantId = this.variantInput?.value;
      let productId = this.dataset.productId;
      let bulk = false;

      if (!this.choices.length) {
        units = this.stepValue();
        const price = int(this.dataset.price);
        total = price * units;
        compareTotal = Math.max(price, int(this.dataset.compare)) * units;
        submitQuantity = units;
        available = this.dataset.available !== 'false';
        if (this.stepInput) this.stepInput.value = String(units);
      } else {
        if (!choice) return;
        bulk = choice.hasAttribute('data-bulk');
        const price = int(choice.dataset.price);
        available = choice.dataset.available !== 'false' && !choice.disabled;
        variantId = choice.value;
        productId = choice.dataset.productId || productId;
        const quantity = this.stepValue();
        if (this.stepInput) this.stepInput.value = String(quantity);
        if (bulk) {
          units = quantity;
          compareTotal = price * units;
          total = Math.round((compareTotal * (100 - this.bulkPercent)) / 100);
          submitQuantity = units;
        } else {
          // The quantity counts packs, so the bottle count and the prices multiply with it.
          units = Math.max(1, int(choice.dataset.units, 1)) * quantity;
          total = price * quantity;
          compareTotal = Math.max(price, int(choice.dataset.compare)) * quantity;
          submitQuantity = quantity;
        }
        if (this.bulkPanel) this.bulkPanel.hidden = !bulk;
      }

      const saving = Math.max(0, compareTotal - total);
      const money = (cents) => formatMoney(cents, this.moneyFormat);
      const label = units === 1 ? fill(this.dataset.unitOne, { count: 1 }) : fill(this.dataset.unitOther, { count: units });

      if (this.variantInput && variantId) this.variantInput.value = variantId;
      if (this.quantityInput) this.quantityInput.value = String(submitQuantity);
      if (this.formComponent && productId) this.formComponent.dataset.productId = productId;

      this.scope.querySelectorAll('[data-zyma-pd-price]').forEach((node) => { node.textContent = money(total); });
      this.scope.querySelectorAll('[data-zyma-pd-compare]').forEach((node) => {
        node.textContent = saving > 0 ? money(compareTotal) : '';
        node.hidden = saving <= 0;
      });
      this.scope.querySelectorAll('[data-zyma-pd-selection]').forEach((node) => { node.textContent = `${label} · ${money(total)}`; });
      this.scope.querySelectorAll('[data-zyma-pd-selection-label]').forEach((node) => { node.textContent = label; });
      this.querySelectorAll('[data-zyma-pd-saving]').forEach((node) => {
        node.textContent = saving > 0 ? `${this.dataset.saveLabel || ''} ${money(saving)}`.trim() : '';
        node.hidden = saving <= 0;
      });
      this.querySelectorAll('[data-zyma-pd-bulk-price]').forEach((node) => {
        node.textContent = bulk ? money(total) : node.dataset.idle || '';
      });

      const button = this.querySelector('[ref="addToCartButton"]');
      if (button instanceof HTMLButtonElement) {
        button.disabled = !available;
        const text = button.querySelector('.add-to-cart-text__content span span');
        if (text) text.textContent = available ? this.dataset.labelAdd : this.dataset.labelSold;
      }

      this.dispatchEvent(new CustomEvent('zyma:purchase-change', {
        bubbles: true,
        detail: { units, quantity: submitQuantity, variantId, price: total, compareAt: compareTotal, saving, available, label }
      }));
    }
  }

  class ZymaPdBar extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const scope = this.closest('[data-zyma-pd]');
      const target = scope?.querySelector('[data-zyma-pd-buy]');
      if (!scope || !target || !('IntersectionObserver' in window)) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      const button = this.querySelector('[data-zyma-pd-bar-submit]');

      button?.addEventListener('click', () => {
        const submit = scope.querySelector('[data-zyma-pd-buy] [ref="addToCartButton"]');
        if (submit instanceof HTMLButtonElement && !submit.disabled) submit.click();
      }, { signal });

      scope.addEventListener('zyma:purchase-change', (event) => {
        if (button instanceof HTMLButtonElement) button.disabled = event.detail?.available === false;
      }, { signal });

      // The room the bar needs is reserved once, while the page has a bar at all. It must not follow the bar's
      // visibility: sections size themselves against it, and they would resize on every scroll past the buy options.
      this.reserveSpace();
      addEventListener('resize', this.reserveSpace, { signal, passive: true });

      this.observer = new IntersectionObserver(([entry]) => {
        // Only after the buy options have scrolled up and out of view, not before reaching them.
        this.setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      });
      this.observer.observe(target);
    }

    reserveSpace = () => {
      const height = Math.ceil(this.getBoundingClientRect().height);
      if (height > 0) document.body.style.setProperty('--zyma-pd-bar-space', `${height}px`);
    };

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.querySelectorAll('button').forEach((button) => { button.tabIndex = visible ? 0 : -1; });
      document.body.classList.toggle('zyma-pd-bar-visible', visible);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      document.body.classList.remove('zyma-pd-bar-visible');
      document.body.style.removeProperty('--zyma-pd-bar-space');
    }
  }

  if (!customElements.get('zyma-pd-purchase')) customElements.define('zyma-pd-purchase', ZymaPdPurchase);
  if (!customElements.get('zyma-pd-bar')) customElements.define('zyma-pd-bar', ZymaPdBar);

  if (window.zymaPdWired) return;
  window.zymaPdWired = true;

  // Pack value table (and any other [data-zyma-pd-units] button): select that option in the buy box.
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-zyma-pd-units]') : null;
    if (!button) return;
    const purchase = document.querySelector('zyma-pd-purchase');
    if (!purchase || typeof purchase.select !== 'function' || !purchase.select(button.dataset.zymaPdUnits)) return;
    const target = purchase.closest('[data-zyma-pd-buy]') || purchase;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    purchase.querySelector('[data-zyma-pd-choice]:checked, [data-zyma-pd-step-input]')?.focus({ preventScroll: true });
  });

  // Theme editor: selecting a collapsible row opens it.
  document.addEventListener('shopify:block:select', (event) => {
    if (event.target instanceof HTMLDetailsElement && event.target.classList.contains('zyma-pd-accordion')) event.target.open = true;
  });
})();
